/**
 * ============================================================================
 * dateVisibility.js - Utilitaire centralisé pour la gestion de visibilité par dates
 * ============================================================================
 * 
 * OBJECTIF:
 * Fournir une logique fiable, robuste et cohérente pour déterminer si un élément
 * doit être visible en fonction de ses dates de début et de fin.
 * 
 * RÈGLES DE VISIBILITÉ:
 * - current_date >= start_date ET current_date <= end_date
 * - start_date absente → visible immédiatement (pas de restriction de début)
 * - end_date absente → visible sans limite (pas de restriction de fin)
 * - Les deux absentes → toujours visible
 * - Dates invalides/mal formatées → visible (fail-safe, pas de crash)
 * 
 * FORMAT DE DATE ATTENDU:
 * - ISO 8601: YYYY-MM-DDTHH:mm:ss ou YYYY-MM-DD
 * - Fuseau horaire: UTC par défaut (dates interprétées comme UTC si pas de timezone)
 * 
 * UTILISATION:
 * ```js
 * import { isVisibleByDate, filterVisibleItems } from '../lib/dateVisibility';
 * 
 * // Simple check
 * if (isVisibleByDate(banner.start_date, banner.end_date)) {
 *   // Afficher la bannière
 * }
 * 
 * // Filtrer un tableau
 * const visibleBanners = filterVisibleItems(banners, 'start_date', 'end_date');
 * ```
 */

// Mode debug: activer pour voir les logs dans la console
const DEBUG_MODE = process.env.NODE_ENV === 'development';

/**
 * Parse une date de manière sécurisée
 * Supporte les formats ISO 8601: YYYY-MM-DD, YYYY-MM-DDTHH:mm:ss, YYYY-MM-DDTHH:mm:ssZ
 * 
 * @param {string|Date|null|undefined} dateValue - La valeur de date à parser
 * @returns {Date|null} - L'objet Date parsé ou null si invalide/absent
 */
export function parseDate(dateValue) {
  // Cas null/undefined/vide
  if (!dateValue) {
    return null;
  }
  
  // Déjà un objet Date valide
  if (dateValue instanceof Date) {
    return isNaN(dateValue.getTime()) ? null : dateValue;
  }
  
  // String - parsing sécurisé
  if (typeof dateValue === 'string') {
    const trimmed = dateValue.trim();
    if (!trimmed) {
      return null;
    }
    
    try {
      // Normaliser le format pour éviter les problèmes de timezone
      // Si la date n'a pas de timezone, ajouter Z (UTC)
      let normalizedDate = trimmed;
      
      // Format YYYY-MM-DD sans heure → ajouter T00:00:00Z
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        normalizedDate = `${trimmed}T00:00:00Z`;
      }
      // Format avec T mais sans Z et sans offset → ajouter Z
      else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(trimmed)) {
        normalizedDate = `${trimmed}Z`;
      }
      // Format DD/MM/YYYY ou DD-MM-YYYY (format français) → convertir
      else if (/^\d{2}[/-]\d{2}[/-]\d{4}$/.test(trimmed)) {
        const parts = trimmed.split(/[/-]/);
        normalizedDate = `${parts[2]}-${parts[1]}-${parts[0]}T00:00:00Z`;
      }
      
      const parsed = new Date(normalizedDate);
      
      // Vérifier que la date est valide
      if (isNaN(parsed.getTime())) {
        if (DEBUG_MODE) {
          console.warn(`[dateVisibility] Date invalide: "${dateValue}"`);
        }
        return null;
      }
      
      return parsed;
    } catch (error) {
      if (DEBUG_MODE) {
        console.error(`[dateVisibility] Erreur parsing date "${dateValue}":`, error);
      }
      return null;
    }
  }
  
  // Timestamp numérique
  if (typeof dateValue === 'number') {
    const parsed = new Date(dateValue);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  
  // Type non supporté
  if (DEBUG_MODE) {
    console.warn(`[dateVisibility] Type de date non supporté:`, typeof dateValue, dateValue);
  }
  return null;
}

/**
 * Obtient la date actuelle en UTC
 * Centralise l'obtention de la date pour faciliter les tests et la cohérence
 * 
 * @returns {Date} - La date actuelle
 */
export function getCurrentDate() {
  return new Date();
}

/**
 * Détermine si un élément est visible selon ses dates de début et de fin
 * 
 * RÈGLES:
 * - Pas de start_date → visible immédiatement
 * - Pas de end_date → visible indéfiniment
 * - current >= start ET current <= end → visible
 * - En cas d'erreur → visible (fail-safe)
 * 
 * @param {string|Date|null} startDate - Date de début de visibilité
 * @param {string|Date|null} endDate - Date de fin de visibilité
 * @param {Date} [referenceDate] - Date de référence (par défaut: maintenant)
 * @returns {boolean} - true si l'élément doit être visible
 */
export function isVisibleByDate(startDate, endDate, referenceDate = null) {
  try {
    const now = referenceDate || getCurrentDate();
    const start = parseDate(startDate);
    const end = parseDate(endDate);
    
    // Cas: aucune date définie → toujours visible
    if (!start && !end) {
      if (DEBUG_MODE) {
        console.log('[dateVisibility] Aucune date définie → visible');
      }
      return true;
    }
    
    // Cas: seulement start_date défini
    if (start && !end) {
      const isVisible = now >= start;
      if (DEBUG_MODE) {
        console.log(`[dateVisibility] Start only: now(${now.toISOString()}) >= start(${start.toISOString()}) = ${isVisible}`);
      }
      return isVisible;
    }
    
    // Cas: seulement end_date défini
    if (!start && end) {
      const isVisible = now <= end;
      if (DEBUG_MODE) {
        console.log(`[dateVisibility] End only: now(${now.toISOString()}) <= end(${end.toISOString()}) = ${isVisible}`);
      }
      return isVisible;
    }
    
    // Cas: les deux dates définies
    const isVisible = now >= start && now <= end;
    if (DEBUG_MODE) {
      console.log(`[dateVisibility] Full range: ${start.toISOString()} <= now(${now.toISOString()}) <= ${end.toISOString()} = ${isVisible}`);
    }
    return isVisible;
    
  } catch (error) {
    // En cas d'erreur, fail-safe: considérer comme visible
    if (DEBUG_MODE) {
      console.error('[dateVisibility] Erreur lors de la vérification, fail-safe → visible:', error);
    }
    return true;
  }
}

/**
 * Filtre un tableau d'éléments selon leur visibilité par dates
 * 
 * @param {Array} items - Tableau d'éléments à filtrer
 * @param {string} startDateKey - Nom de la propriété contenant la date de début
 * @param {string} endDateKey - Nom de la propriété contenant la date de fin
 * @param {Date} [referenceDate] - Date de référence (par défaut: maintenant)
 * @returns {Array} - Éléments visibles uniquement
 */
export function filterVisibleItems(items, startDateKey = 'start_date', endDateKey = 'end_date', referenceDate = null) {
  if (!Array.isArray(items)) {
    if (DEBUG_MODE) {
      console.warn('[dateVisibility] filterVisibleItems: items n\'est pas un tableau');
    }
    return [];
  }
  
  return items.filter(item => {
    if (!item || typeof item !== 'object') {
      return false;
    }
    
    const startDate = item[startDateKey];
    const endDate = item[endDateKey];
    
    return isVisibleByDate(startDate, endDate, referenceDate);
  });
}

/**
 * Enrichit un élément avec des informations de visibilité
 * Utile pour le debug ou l'affichage dans l'admin
 * 
 * @param {Object} item - L'élément à enrichir
 * @param {string} startDateKey - Nom de la propriété date de début
 * @param {string} endDateKey - Nom de la propriété date de fin
 * @returns {Object} - Élément enrichi avec _visibility
 */
export function enrichWithVisibilityInfo(item, startDateKey = 'start_date', endDateKey = 'end_date') {
  if (!item || typeof item !== 'object') {
    return item;
  }
  
  const now = getCurrentDate();
  const start = parseDate(item[startDateKey]);
  const end = parseDate(item[endDateKey]);
  const isVisible = isVisibleByDate(start, end, now);
  
  return {
    ...item,
    _visibility: {
      isVisible,
      hasStartDate: !!start,
      hasEndDate: !!end,
      startDate: start?.toISOString() || null,
      endDate: end?.toISOString() || null,
      currentDate: now.toISOString(),
      status: !start && !end 
        ? 'always_visible' 
        : isVisible 
          ? 'visible' 
          : now < (start || now) 
            ? 'scheduled' 
            : 'expired'
    }
  };
}

/**
 * Vérifie si un bloc CMS (banner, carousel, etc.) est visible
 * Supporte la structure content.start_date / content.end_date
 * 
 * @param {Object} block - Le bloc CMS
 * @returns {boolean} - true si le bloc est visible
 */
export function isBlockVisibleByDate(block) {
  if (!block) {
    return false;
  }
  
  // Vérifier d'abord is_active
  if (block.is_active === false) {
    return false;
  }
  
  // Les dates peuvent être dans content ou directement sur le bloc
  const content = block.content || {};
  const startDate = content.start_date || block.start_date;
  const endDate = content.end_date || block.end_date;
  
  return isVisibleByDate(startDate, endDate);
}

/**
 * Filtre un tableau de bannières pour le carousel
 * Combine les vérifications is_active et dates
 * Supporte les deux formats: start_date/end_date ET startDate/endDate (rétrocompatibilité)
 * 
 * @param {Array} banners - Tableau de bannières
 * @returns {Array} - Bannières visibles
 */
export function filterVisibleBanners(banners) {
  if (!Array.isArray(banners)) {
    return [];
  }
  
  const now = getCurrentDate();
  
  return banners.filter(banner => {
    const bannerId = banner.title || banner.id || 'unknown';
    
    // 1. PRIORITÉ MAXIMALE: Vérifier is_active en premier
    if (banner.is_active === false) {
      if (DEBUG_MODE) {
        console.log(`[dateVisibility] Banner "${bannerId}" ignorée: is_active=false`);
      }
      return false;
    }
    
    // 2. Récupérer les dates (supporte les deux formats pour rétrocompatibilité)
    const startDate = banner.start_date || banner.startDate || null;
    const endDate = banner.end_date || banner.endDate || null;
    
    // 3. Vérifier la visibilité par dates
    const isVisible = isVisibleByDate(startDate, endDate, now);
    
    // 4. Log détaillé en mode debug
    if (DEBUG_MODE) {
      const startParsed = parseDate(startDate);
      const endParsed = parseDate(endDate);
      console.log(`[dateVisibility] Banner "${bannerId}": ` +
        `is_active=true, ` +
        `start=${startDate || 'N/A'} (parsed: ${startParsed?.toISOString() || 'null'}), ` +
        `end=${endDate || 'N/A'} (parsed: ${endParsed?.toISOString() || 'null'}), ` +
        `now=${now.toISOString()}, ` +
        `visible=${isVisible}`
      );
    }
    
    return isVisible;
  });
}

// Export par défaut pour import simplifié
export default {
  parseDate,
  getCurrentDate,
  isVisibleByDate,
  filterVisibleItems,
  enrichWithVisibilityInfo,
  isBlockVisibleByDate,
  filterVisibleBanners
};
